import { useEffect } from "react";
import { toast } from "react-toastify";
import { useVersionCheck } from "../../hooks/useVersionCheck";
import styles from "./UpdateNotifier.module.css";

/**
 * UpdateNotifier — avisa que saiu uma versão nova enquanto a aba está aberta.
 *
 * Não renderiza nada: consome `useVersionCheck` e dispara UM toast persistente
 * pelo `<ToastContainer>` que já existe no App (não montar outro container).
 *
 * Decisões:
 * - `toastId` fixo → nunca duplica, mesmo se o efeito reexecutar (StrictMode).
 * - `autoClose: false` / `closeOnClick: false` / `draggable: false` → o aviso
 *   não some sozinho nem por clique acidental; sair é decisão explícita do
 *   usuário (botão × do react-toastify).
 * - Dispensar grava a versão em `sessionStorage`. Isso protege o caso em que a
 *   página é recarregada e o navegador/CDN ainda serve o bundle antigo (o
 *   `index.html` continua em cache): o hook detecta a divergência de novo e, sem
 *   o carimbo, o toast voltaria a cada reload depois de o usuário já ter dito
 *   "agora não". `sessionStorage` (não `localStorage`) porque o escopo certo é a
 *   aba, não o navegador para sempre.
 *
 * Limitação conhecida (deliberada): é UM aviso por aba, dispensável.
 * `useVersionCheck` chama `stop()` na primeira detecção e não retoma o polling,
 * então `latestVersion` congela naquela aba — um SEGUNDO deploy na mesma aba não
 * gera aviso novo. O aviso já está (ou já esteve) na tela; insistir não muda
 * nada, e recarregar resolve os dois deploys de uma vez.
 */
const TOAST_ID = "app-update";
const DISMISSED_KEY = "nexus:update-dismissed-version";

function readDismissedVersion() {
  try {
    return window.sessionStorage.getItem(DISMISSED_KEY);
  } catch {
    // sessionStorage bloqueado (modo privado/cookies off): sem persistência,
    // o aviso simplesmente não é lembrado entre reloads. Não é erro.
    return null;
  }
}

function rememberDismissedVersion(version) {
  try {
    window.sessionStorage.setItem(DISMISSED_KEY, version);
  } catch {
    // Idem: falha de storage nunca pode estourar dentro de um callback de toast.
  }
}

/**
 * Conteúdo do toast. O react-toastify aceita um componente como `content` e o
 * renderiza com `{ closeToast, toastProps, data }`.
 */
export function UpdateToastContent() {
  return (
    <div className={styles.content}>
      <p className={styles.message}>
        Saiu uma versão nova do Nexus. Clica pra atualizar — leva 2 segundos.
      </p>
      <button
        type="button"
        className={styles.action}
        onClick={() => window.location.reload()}
      >
        Atualizar agora
      </button>
    </div>
  );
}

export default function UpdateNotifier() {
  const { updateAvailable, latestVersion } = useVersionCheck();

  useEffect(() => {
    if (!updateAvailable) return;
    if (latestVersion && readDismissedVersion() === latestVersion) return;
    if (toast.isActive(TOAST_ID)) return;

    toast(UpdateToastContent, {
      toastId: TOAST_ID,
      position: "bottom-center",
      autoClose: false,
      closeOnClick: false,
      draggable: false,
      pauseOnFocusLoss: false,
      hideProgressBar: true,
      onClose: () => {
        if (latestVersion) rememberDismissedVersion(latestVersion);
      },
    });
  }, [updateAvailable, latestVersion]);

  return null;
}
